import {APPLY_IRREGULAR_PATTERN, RESIDENT_APPLY_PROCESS} from '../../../../../CodeMaster'
import React, {Component, ReactNode} from 'react'
import { Box, DialogContent, Typography } from '@mui/material';
import {RowFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {NotifyDialogTitle} from '../../../../../components/assocDirector/AssocDirectorCommon'
import {StyledPre} from '../../../../../styledComponents/adminPage/StyledPre'
import {DialogCloseButton} from '../../../../../components/reservations/components/CommonStyle'
import {Close} from '@mui/icons-material'
import {RequiredChip} from '../../../../../styledComponents/adminPage/Table'
import EventEmitterExAdmin from '../../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {
    ApplyAlertButton,
    ApplyDialogActions,
    FormLabelContainer,
    OutlinedTextArea,
    SpApplyDialogTitle,
    SpApplyDialogTitleFont,
    SpDialogApplyProcessTag,
    StyledApplyFormLabel,
    StyledHeader,
    StyledHeadingHr,
    StyledPageFooterArea,
} from './NatAdminApplicationCommon'
import {PageFotterAreaRight} from '../../../../../components/shared/hoc/withAdminPageTemplate'
import NatAdminCommonAcceptedWarningComponent from '../../../common/admin/NatAdminCommonAcceptedWarningComponent'
import {Messages} from '../../../../../Messages'
import {BoldSpan} from '../../../../../styledComponents/StyledSpan'
import {AdminDialog} from '../../../../../components/shared/pageBaseComponents/AdminDialog'
import {AppAdminContext} from '../../../../../app/AppAdminContext'

interface Props {
    open: boolean
    residentApplyProcess: RESIDENT_APPLY_PROCESS
    applyIrregularPattern?: APPLY_IRREGULAR_PATTERN
    onChange: (value: string) => void
    onRejection: () => Promise<void>
    onCancel: () => void
    eventEmitter: EventEmitterExAdmin
}

interface State {
    comment: string
    isAccepted: boolean
    rejectionButtonDisabled: boolean
}

export default class NatAdminApplicationRejectionDialog extends Component<Props, State> {
    declare context: React.ContextType<typeof AppAdminContext>
    constructor(props: Readonly<Props>) {
        super(props)
        this.state = {
            comment: '',
            isAccepted: false,
            rejectionButtonDisabled: true,
        }

        this.handleMemoChange = this.handleMemoChange.bind(this)
        this.handleChangeWarningCheckBox = this.handleChangeWarningCheckBox.bind(this)
    }

    componentDidUpdate(prevProps: Props): void {
        if (prevProps.open !== this.props.open) {
            this.setState({
                comment: '',
                isAccepted: false,
                rejectionButtonDisabled: true,
            })
        }
    }

    handleMemoChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({comment: event.target.value}, () => {
            this.props.onChange(this.state.comment)
            this.updateRejectionButtonDisabled()
        })
    }

    handleChangeWarningCheckBox(acceptAll: boolean): void {
        this.setState({isAccepted: acceptAll}, () => {
            this.updateRejectionButtonDisabled()
        })
    }

    updateRejectionButtonDisabled(): void {
        const {comment, isAccepted} = this.state
        if (comment && isAccepted) {
            this.setState({rejectionButtonDisabled: false})
        } else {
            this.setState({rejectionButtonDisabled: true})
        }
    }

    renderDescription(residentApplyProcess: RESIDENT_APPLY_PROCESS,
                      applyIrregularPattern?: APPLY_IRREGULAR_PATTERN,
    ): ReactNode {
        switch (residentApplyProcess) {
            case RESIDENT_APPLY_PROCESS.ASSOC_DIRECTOR_APPROVAL:
                if (applyIrregularPattern === APPLY_IRREGULAR_PATTERN.ASSOC_DIRECTOR_DELEGATE_APPROVAL) {
                    return <>
                        <div>この申請を理事長の代わりに却下します。</div>
                        <div>却下操作の取り消しはできません。申請内容に見落としがないか十分にご確認ください。</div>
                    </>
                } else {
                    return <>
                        <div>この申請を却下します。</div>
                        <div>却下操作の取り消しはできません。申請内容に見落としがないか十分にご確認ください。</div>
                        <div>また、<BoldSpan>却下後は申請内容を確認できなくなりますのでご注意ください。</BoldSpan></div>
                    </>
                }
            case RESIDENT_APPLY_PROCESS.PET_CHAIRMAN_APPROVAL:
                return <>
                    <div>この申請をペットクラブ委員長の代わりに却下します。</div>
                    <div>却下操作の取り消しはできません。申請内容に見落としがないか十分にご確認ください。</div>
                </>
            default:
                return <>
                    <div>この申請を却下します。</div>
                    <div>却下操作の取り消しはできません。申請内容に見落としがないか十分にご確認ください。</div>
                </>
        }
    }

    renderApplyProcessTag(
        residentApplyProcess: RESIDENT_APPLY_PROCESS,
        applyIrregularPattern?: APPLY_IRREGULAR_PATTERN,
    ): ReactNode {
        switch (residentApplyProcess) {
            case RESIDENT_APPLY_PROCESS.ASSOC_DIRECTOR_APPROVAL:
                if (applyIrregularPattern === APPLY_IRREGULAR_PATTERN.ASSOC_DIRECTOR_DELEGATE_APPROVAL) {
                    return (
                        <SpDialogApplyProcessTag>理事長代行</SpDialogApplyProcessTag>
                    )
                } else {
                    return null
                }
            case RESIDENT_APPLY_PROCESS.PET_CHAIRMAN_APPROVAL:
                if (applyIrregularPattern === APPLY_IRREGULAR_PATTERN.NO_PET_CLUB) {
                    return (
                        <SpDialogApplyProcessTag>ペットクラブなし</SpDialogApplyProcessTag>
                    )
                } else {
                    return (
                        <SpDialogApplyProcessTag>ペットクラブ委員長代行</SpDialogApplyProcessTag>
                    )
                }
            default:
                return null
        }
    }

    renderDialogContent(): ReactNode {
        const {residentApplyProcess, applyIrregularPattern} = this.props
        const {comment} = this.state

        return <>
            <Box>
                {this.renderApplyProcessTag(residentApplyProcess, applyIrregularPattern)}
            </Box>
            <Box mb={'16px'}>
                <StyledPre>
                    {this.renderDescription(residentApplyProcess, applyIrregularPattern)}
                </StyledPre>
            </Box>
            <FormLabelContainer>
                <StyledApplyFormLabel>
                    却下理由
                </StyledApplyFormLabel>
                <Box ml={'4px'}><RequiredChip label={'必須'}/></Box>
            </FormLabelContainer>
            <OutlinedTextArea
                InputProps={{
                    inputProps: {
                        maxLength: 200,
                        minRows: 6,
                    },
                }}
                required
                fullWidth
                multiline
                value={comment}
                placeholder={'200文字以内'}
                eventKeys={['comment']}
                eventEmitter={this.props.eventEmitter}
                onChange={this.handleMemoChange}
            />
            <Box mt={'8px'} fontSize={'12px'}>
                ・申請者は、メールや申請履歴から却下理由を確認することができます。<br/>
                ・申請却下後は却下理由を編集・削除することができません。ご注意ください。
            </Box>
            <Box mt={'16px'}>
                <NatAdminCommonAcceptedWarningComponent
                    description={'内容をご確認いただき、問題なければチェックしたうえで却下ボタンを押してください。'}
                    warnings={[{
                        messageId: 'WPCDA0001',
                        message: Messages.WPCDA0001.toString(),
                    }]}
                    changeAcceptedWarnings={this.handleChangeWarningCheckBox}
                />
            </Box>
        </>
    }

    renderPc(): ReactNode {
        const {onRejection, onCancel} = this.props
        const {rejectionButtonDisabled} = this.state

        return <Box width={'750px'}>
            <NotifyDialogTitle>
                <StyledHeader>
                    <RowFlexContainer>
                        <Typography variant="h5" display={'inline'}>
                            申請の却下
                        </Typography>
                        <DialogCloseButton onClick={onCancel} id={'close'}>
                            <Close/>
                        </DialogCloseButton>
                    </RowFlexContainer>
                </StyledHeader>
                <StyledHeadingHr/>
            </NotifyDialogTitle>
            <DialogContent>{this.renderDialogContent()}</DialogContent>
            <ApplyDialogActions>
                <StyledPageFooterArea>
                    <PageFotterAreaRight>
                        <ApplyAlertButton onClick={onRejection} disabled={rejectionButtonDisabled}>
                            この申請を却下する
                        </ApplyAlertButton>
                    </PageFotterAreaRight>
                </StyledPageFooterArea>
            </ApplyDialogActions>
        </Box>
    }

    renderSp(): ReactNode {
        const {onRejection, onCancel} = this.props
        const {rejectionButtonDisabled} = this.state

        return <>
            <SpApplyDialogTitle>
                <RowFlexContainer>
                    <SpApplyDialogTitleFont>
                        申請の却下
                    </SpApplyDialogTitleFont>
                    <DialogCloseButton onClick={onCancel}>
                        <Close fontSize={'large'}/>
                    </DialogCloseButton>
                </RowFlexContainer>
            </SpApplyDialogTitle>
            <DialogContent>{this.renderDialogContent()}</DialogContent>
            <ApplyDialogActions>
                <ApplyAlertButton
                    onClick={onRejection}
                    disabled={rejectionButtonDisabled}
                    width={'100%'}
                >
                    この申請を却下する
                </ApplyAlertButton>
            </ApplyDialogActions>
        </>
    }

    render(): ReactNode {
        const spMode = this.context.showSpModePage

        return (
            <AdminDialog maxWidth={'md'} open={this.props.open}>
                {spMode ? this.renderSp() : this.renderPc()}
            </AdminDialog>
        )
    }
}

NatAdminApplicationRejectionDialog.contextType = AppAdminContext
